import { format_fullMonth_from_ISOShort, getTimeDifference, formatISOShortToDDMonth } from "@/timeFunctions.js";
import { getPricingRules } from '@/pricing/repository/pricingRepository.js'

export const breakDownRentCosts = (terms, fees = {}) => {
    return new Promise((resolve, reject) => {
        var timeDiff = getTimeDifference(terms.periodStart, terms.periodEnd)
        var pricingRules
        return getPricingRules()
            .then(rules => {
                pricingRules = rules
            })
            .catch(err => {
                pricingRules = fallbackRules
            })
            .finally(hi => {
                resolve(breakdown(terms, timeDiff, pricingRules, fees))
            })
    })
}

const breakdown = (terms, timeDiff, pricingRules, fees = {}) => {
    var costBreakdown = {
        totalBeforeMovingIn: undefined,
        dailyRentTotal: undefined,
        rentForFirstMonth: undefined,
        preDaysRent: {
            total: undefined,
            daily: undefined
        },
        postDaysRent: {
            total: undefined,
            daily: undefined
        },
        preMonth: undefined,
        postMonth: undefined,
        stayTimeBreakdown: timeDiff,
        price: undefined,
        discountedPrice: undefined,
        deposit: undefined,
        additionalCostsTotal: undefined,
        bookingFee: undefined
    }

    costBreakdown.additionalCostsTotal = sumUpAdditionalCosts(terms.additionalFees)
    costBreakdown.price = Number(terms.price)
    costBreakdown.deposit = Number(terms.deposit)

    var totalBeforeMovingIn = 0
    var breakdown
    if (terms.monthly) {
        breakdown = calcMonthly_breakdown(terms, timeDiff, pricingRules)
        if(breakdown.rentForFirstMonth) costBreakdown.rentForFirstMonth = breakdown.rentForFirstMonth
        
        var preMonthValue
        if(terms.accountingPeriod && terms.accountingPeriod.startDay && terms.accountingPeriod.startDay > 1){
            preMonthValue = formatISOShortToDDMonth(terms.periodStart) + " - " + terms.accountingPeriod.startDay + "th next month" ;
        }else {
            preMonthValue = format_fullMonth_from_ISOShort(terms.periodStart);
        }
        costBreakdown.preMonth = preMonthValue
        terms.periodEnd ? costBreakdown.postMonth = format_fullMonth_from_ISOShort(terms.periodEnd) : void 0;

        costBreakdown.preDaysRent = breakdown.rentForPreDays;
        costBreakdown.postDaysRent = breakdown.rentForPostDays;

        costBreakdown.preDaysRent ? totalBeforeMovingIn += costBreakdown.preDaysRent.total : void 0;
        costBreakdown.rentForFirstMonth ? totalBeforeMovingIn += costBreakdown.rentForFirstMonth : void 0;

    } else {
        breakdown = calcDaily_breakdown(terms, timeDiff, pricingRules)
        if (timeDiff.totalNrOfDays) {
            costBreakdown.dailyRentTotal = Number(breakdown.discountedPrice * timeDiff.totalNrOfDays);
            costBreakdown.dailyRentTotal ? totalBeforeMovingIn += costBreakdown.dailyRentTotal : void 0;
        }
    }

    costBreakdown.deposit ? totalBeforeMovingIn += costBreakdown.deposit : void 0;
    costBreakdown.additionalCostsTotal ? totalBeforeMovingIn += costBreakdown.additionalCostsTotal : void 0;

    if (costBreakdown.price > breakdown.discountedPrice) costBreakdown.discountedPrice = breakdown.discountedPrice;
    costBreakdown.totalBeforeMovingIn = Number(totalBeforeMovingIn)
    costBreakdown.bookingFee = calcMoowleBookingFee(calcTotalDealWorth(costBreakdown), pricingRules, fees);
    
    return costBreakdown
}

const calcMoowleBookingFee = (totalDealWorth, pricingRules, fees = {}) => {
    const moowleRentFeePercentage = !isNaN(fees.bookingFeePercentage) ? fees.bookingFeePercentage : pricingRules.min_moowle_rent_fee_percentage;
    const maxMoowleRentFee = !isNaN(fees.bookingFeeValue) ? fees.bookingFeeValue : pricingRules.min_moowle_rent_fee;
    var precentFromTotal = (totalDealWorth * moowleRentFeePercentage) / 100
    if (precentFromTotal < maxMoowleRentFee) return precentFromTotal.toFixed(2)
    else return maxMoowleRentFee
}

const calcTotalDealWorth = (costBreakdown) => {
    var total = 0
    if (costBreakdown.dailyRentTotal) {
        // indicates daily term (with daily rent, total deal is payd up front thus dailyRentTotal is not undefiend)
        total = costBreakdown.totalBeforeMovingIn
    } else {
        // indicates monthly term
        total = costBreakdown.totalBeforeMovingIn + // includes preDaysRent
            costBreakdown.postDaysRent.total;
        if (costBreakdown.discountedPrice) total += costBreakdown.discountedPrice * costBreakdown.stayTimeBreakdown.months;
        else total += costBreakdown.price * costBreakdown.stayTimeBreakdown.months;
    }
    return total;
}

const calcMonthly_breakdown = (terms, timeDiff, pricingRules) => {
    var monthlyPrice = applyDiscountToMonthly(terms, timeDiff, pricingRules)

    var dailyRentForPreDays = 0, totalRentForPreDays = 0, dailyRentForPostDays = 0, totalRentForPostDays = 0;
    dailyRentForPreDays = (monthlyPrice / timeDiff.preDays.daysInMonth)
    if(terms.accountingPeriod && terms.accountingPeriod.startDay && terms.accountingPeriod.startDay > 1){
        // Custom accounting
        var rentForThisMonth = timeDiff.preDays.nrOfDaysStaying * dailyRentForPreDays
        var rentForNextMonth = (terms.accountingPeriod.endDay-1) * dailyRentForPreDays
        totalRentForPreDays = rentForThisMonth + rentForNextMonth
    }else {
        // Calendar month accounting
        totalRentForPreDays = (timeDiff.preDays.nrOfDaysStaying * dailyRentForPreDays)
    }

    if (timeDiff.postDays.nrOfDaysStaying) {
        dailyRentForPostDays = (monthlyPrice / timeDiff.postDays.daysInMonth)
        totalRentForPostDays = (timeDiff.postDays.nrOfDaysStaying * dailyRentForPostDays)
    }

    var r = {
        rentForPreDays: { total: Number(totalRentForPreDays), daily: Number(dailyRentForPreDays) },
        rentForPostDays: { total: Number(totalRentForPostDays), daily: Number(dailyRentForPostDays) },
        discountedPrice: monthlyPrice
    }
    if(!r.rentForPreDays.total) r.rentForFirstMonth = monthlyPrice

    return r
}

function calcDaily_breakdown(terms, timeDiff, pricingRules) {
    var rentDaily = applyDiscoundToDaily(terms, timeDiff, pricingRules)
    return {
        discountedPrice: rentDaily
    }
}

const applyDiscountToMonthly = (terms, timeDiff, pricingRules) => {
    var baseRent = Number(terms.price)
    var discounts = terms.discounts

    if (!discounts || !timeDiff.totalNrOfDays) return baseRent

    if (timeDiff.totalNrOfDays > pricingRules.max_medium_rent_period_in_days && discounts.longPeriodDiscount)
        return (baseRent - (baseRent * discounts.longPeriodDiscount / 100))

    if (timeDiff.totalNrOfDays > pricingRules.max_base_rent_period_in_days && discounts.mediumPeriodDiscount)
        return (baseRent - (baseRent * discounts.mediumPeriodDiscount / 100))

    return baseRent
}

const applyDiscoundToDaily = (terms, timeDiff, pricingRules) => {
    var baseRent = Number(terms.price)
    var discounts = terms.discounts
    if (!discounts || !timeDiff.totalNrOfDays) return baseRent

    if (timeDiff.totalNrOfDays >= pricingRules.min_weekly_discount_days && discounts.weeklyDiscount)
        return (baseRent - (baseRent * discounts.weeklyDiscount / 100))
    return baseRent
}

const sumUpAdditionalCosts = (addCosts) => {
    var total = 0
    if (!addCosts) return total
    for (var i = 0; i < addCosts.length; i++) {
        total += Number(addCosts[i].fee)
    }
    return total;
}




let fallbackRules = {
    'min_weekly_discount_days': 7,
    'max_short_rent_period_in_days': 30,
    'max_base_rent_period_in_days': 91,
    'max_medium_rent_period_in_days': 183,
    'max_long_rent_period_in_days': -1,
    'daily_rate_applied': ['max_short_rent_period_in_days'],
    'montlhy_rate_applied': ['max_base_rent_period_in_days', 'max_medium_rent_period_in_days', 'max_long_rent_period_in_days'],
    'max_days_when_cleaning_fee': 31,
    'max_days_when_essentials_fee': 31,
    'owner_fees': ['cleaning', 'essentials', 'rent', 'utility'],
    'moowle_fees': ['booking'],
    'min_moowle_rent_fee': 190,
    'max_moowle_rent_fee':400,
    'min_moowle_rent_fee_percentage': 10,
    'moowle_rent_fee_percentage': 50,
}


// WEBPACK FOOTER //
// ./src/platform/payments/rent/strategies/rentPaymentBreakdownStrategy.js